import { OrientedBoundingBox } from "@/components/r3f/utils/oriented-bounding-box";
import { LodPointCloud, PotreeTree } from "@faro-lotv/lotv";
import { filterPointsInRegion } from "./filter-points-in-region";
import { LodPointsSample } from "./sample-lod-points";

/** Full resolution size of a region of a point cloud */
export type RegionPointCount = {
  points: number;
  /** Bytes per point of the stored point cloud, with all its attributes */
  bytesPerPoint: number;
};

/**
 * Estimates how many points of the full resolution point cloud are in a region,
 * from the share of the sampled points in the region on the LOD levels known for the whole tree.
 *
 * @param pointCloud The point cloud the sample was taken from
 * @param sample Points sampled from it, covering the region
 * @param region World region of the export
 * @returns the estimated count, or undefined if the tree doesn't describe its full resolution
 */
export function estimateRegionPointCount(
  pointCloud: LodPointCloud,
  sample: Pick<LodPointsSample, "positions" | "origin" | "levelEnds">,
  region: OrientedBoundingBox,
): RegionPointCount | undefined {
  const { tree } = pointCloud;
  if (!(tree instanceof PotreeTree) || !sample.levelEnds.length) return;
  const { metadata } = tree;

  // The levels of the first hierarchy chunk are known for the whole tree, deeper ones only where they were loaded
  const depth = Math.min(
    sample.levelEnds.length - 1,
    metadata.hierarchy.stepSize,
  );
  const pointsUpToDepth = tree.nodes
    .filter((node) => node.depth <= depth)
    .reduce((sum, node) => sum + node.numPoints, 0);
  if (!pointsUpToDepth) return;

  const sampledInRegion =
    filterPointsInRegion(
      sample.positions.subarray(0, sample.levelEnds[depth] * 3),
      sample.origin,
      region,
    ).length / 3;
  return {
    points: Math.round((metadata.points * sampledInRegion) / pointsUpToDepth),
    bytesPerPoint: metadata.attributes.reduce((sum, { size }) => sum + size, 0),
  };
}
